import type {
  JobType,
  WorkMode,
  JobStatus,
  ScrapingStatus,
  DuplicateStatus,
  AssignmentType,
  RecommendationStatus,
} from '../enums';
import type { PaginationConfig } from './common';

export interface SalaryRange {
  min: number;
  max: number;
  currency: string;
}

export interface JobEmployee {
  id: string;
  name: string;
  role: string;
}

export interface JobApi {
  id: string;
  title: string;
  company: string;
  location: string;
  industry: string;
  jobType: JobType;
  workMode: WorkMode;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  category: string;
  skills: string[];
  experienceMin?: number;
  experienceMax?: number;
  qualifications?: string[];
  description: string;
  requirements?: string;
  benefits?: Record<string, unknown>;
  applicationUrl: string;
  expiryDate?: Date;
  /** Original date the job was posted on the source portal (schema.org datePosted). */
  postedDate?: Date;
  status: JobStatus;
  postedBy: JobEmployee;
  source?: string;
  /** Company logo URL, when the source platform provided one (mostly LinkedIn). */
  companyLogo?: string;
  viewCount: number;
  createdAt: Date;
  updatedAt: Date;
  expiredAt?: Date;
  expiredBy?: string;
  suggestedCategory?: string;
  categoryConfidence?: number;
  jobKey?: number;
}

// Admin uses `Job` — alias for backward compat
export type Job = JobApi;

export interface JobFormData {
  // Basic Info
  title: string;
  company: string;
  location: string;
  industry?: string;

  // Job Details
  jobType: JobType;
  workMode: WorkMode;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  category?: string;
  skills?: string[];
  experienceMin?: number;
  experienceMax?: number;
  qualifications?: string[];

  // Requirements
  description: string;
  requirements?: string;
  benefits?: string | Record<string, unknown>;

  // Application
  applicationUrl: string;
  expiryDate?: Date;
  status?: JobStatus;
  source?: string;

  // Scraping (optional) - frontend-only fields
  useUrlScraping?: boolean;
  scrapingQueueId?: string;
}

export interface DuplicateMatch {
  jobId: string;
  title: string;
  company: string;
  location?: string;
  similarity: number;
  source: 'manual' | 'scraped';
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  matches: DuplicateMatch[];
}

export interface ScrapedJobData {
  title?: string;
  company?: string;
  location?: string;
  description?: string;
  requirements?: string;
  skills?: string[];
  salary?: SalaryRange;
  jobType?: JobType;
  workMode?: WorkMode;
  experience?: string;
  qualifications?: string[];
}

export interface ScrapingQueueItem {
  id: string;
  sourceUrl: string;
  employee: JobEmployee;
  status: ScrapingStatus;
  priority: number;
  jobId?: string;

  formInputData?: Partial<JobFormData>;
  scrapedData?: ScrapedJobData;
  extractionConfidence?: number;
  matchScore?: number;
  duplicateCheckResult?: DuplicateCheckResult;

  jobKey?: number;

  retryCount: number;
  maxRetries: number;
  errorMessage?: string;

  /**
   * Terminal result of a `completed` scrape, so the UI can explain why a
   * completed item did or didn't add a job:
   *   - 'inserted'  a new active job was created
   *   - 'duplicate' a fuzzy cross-platform match, stored as a draft for review
   *   - 'exists'    the same posting was already stored (skipped)
   * Undefined while pending/processing/failed, or for manual-worker rows.
   */
  outcome?: 'inserted' | 'duplicate' | 'exists';

  createdAt: Date;
  processingStartedAt?: Date;
  processedAt?: Date;
}

export interface ScrapingResult {
  queueId: string;
  status: ScrapingStatus;
  scrapedData?: ScrapedJobData;
  extractionConfidence?: number;
  matchScore?: number;
  duplicateWarning?: DuplicateCheckResult;
  errorMessage?: string;
}

export interface JobStats {
  total: number;
  active: number;
  draft: number;
  expired: number;
  totalUsers: number;
  totalCustomers: number;
  totalResumes: number;
  thisWeekJobs: number;
  lastWeekJobs: number;
  byStatus: { status: string; count: number }[];
  byJobType: { jobType: string; count: number }[];
  byWorkMode: { workMode: string; count: number }[];
  byCategory: { category: string; count: number }[];
  byLocation: { location: string; count: number }[];
  byEmployee: { employeeId: string; employeeName: string; employeeRole: string; count: number }[];
  byDate: { date: string; count: number }[];
  /**
   * Jobs grouped by scraping-source prefix (the token before the first `-` in
   * the `source` column, e.g. `li` for LinkedIn). Admin-created jobs have a null
   * source and are bucketed under `manual`. Prefixes map to display labels via
   * the admin's JOB_SOURCES table.
   */
  bySource: { source: string; count: number }[];
}

export interface QueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  avgProcessingTime: number; // seconds
  successRate: number; // percentage
  // Outcome breakdown of completed scrapes (see ScrapingQueueItem.outcome).
  inserted?: number; // new active jobs created
  duplicate?: number; // fuzzy cross-platform matches stored as drafts
  exists?: number; // same posting already stored (a "clash")
}

export interface DuplicateGroup {
  id: string;
  jobs: Job[];
  similarity: number;
  status: DuplicateStatus;
  resolution?: {
    keptJobId: string;
    removedJobIds: string[];
    resolvedBy: JobEmployee;
    resolvedAt: Date;
    comments?: string;
  };
}

export interface JobComparison {
  jobs: Job[];
  differences: {
    field: string;
    values: string[];
  }[];
  recommendations: {
    action: 'keep' | 'merge' | 'delete';
    jobId: string;
    reason: string;
  }[];
}

export interface JobFilters {
  status?: JobStatus | 'all' | 'duplicate';
  scoutId?: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
  category?: string;
  location?: string;
  industry?: string[];
  jobTitle?: string[];
  jobType?: JobType;
  workMode?: WorkMode;
  search?: string;
  /**
   * Which platform a job was fetched from, derived from the `source` prefix
   * (li-/in-/zr-/by-/nk-). 'manual' matches admin-created jobs (no source).
   */
  source?: 'linkedin' | 'indeed' | 'ziprecruiter' | 'bayt' | 'naukri' | 'manual';
}

/** Canonical key for a job's originating platform (mirrors `JobFilters.source`). */
export type JobSourceKey = NonNullable<JobFilters['source']>;

export interface JobSourceMeta {
  key: JobSourceKey;
  label: string;
  /** `source` column prefix in the DB (e.g. "li"); `null` for admin-created (manual) jobs. */
  prefix: string | null;
}

/**
 * Single source of truth for the platform's job-source taxonomy — shared by the
 * admin (table badges + filters), the API (dashboard source breakdowns), and the
 * web app, so labels/keys/prefixes never drift across apps. A source listed here
 * with no jobs feeding it (e.g. `naukri` until its scraper ships) is a KNOWN but
 * not-yet-connected platform, which callers can surface truthfully instead of
 * fabricating a zero.
 */
export const JOB_SOURCES: JobSourceMeta[] = [
  { key: 'linkedin', label: 'LinkedIn', prefix: 'li' },
  { key: 'indeed', label: 'Indeed', prefix: 'in' },
  { key: 'ziprecruiter', label: 'ZipRecruiter', prefix: 'zr' },
  { key: 'bayt', label: 'Bayt', prefix: 'by' },
  { key: 'naukri', label: 'Naukri', prefix: 'nk' },
  { key: 'manual', label: 'Manual', prefix: null },
];

const JOB_SOURCE_MANUAL = JOB_SOURCES[JOB_SOURCES.length - 1]!;
const JOB_SOURCE_BY_PREFIX = new Map(
  JOB_SOURCES.filter((s) => s.prefix).map((s) => [s.prefix as string, s])
);

/**
 * Resolve a job's raw `source` string (e.g. "li-4421939058") to its platform meta.
 * Splits on the first `-` and looks up the prefix; falls back to Manual for
 * admin-created jobs (null/empty/unknown source).
 */
export function jobSourceOf(source?: string | null): JobSourceMeta {
  if (source) {
    const meta = JOB_SOURCE_BY_PREFIX.get(source.split('-')[0]!);
    if (meta) return meta;
  }
  return JOB_SOURCE_MANUAL;
}

export interface QueueFilters {
  status?: ScrapingStatus[];
  /** Terminal outcome of completed scrapes: 'inserted' | 'duplicate' | 'exists'. */
  outcome?: ('inserted' | 'duplicate' | 'exists')[];
  employeeId?: string;
  jobId?: string;
  jobKey?: number;
  search?: string;
  dateRange?: {
    from: Date;
    to: Date;
  };
  priority?: number;
}

export interface DuplicateFilters {
  minSimilarity: number;
  status?: DuplicateStatus | 'all';
  dateRange?: {
    from: Date;
    to: Date;
  };
}

export interface JobSortConfig {
  column: 'title' | 'company' | 'location' | 'category' | 'status' | 'postedBy' | 'createdAt' | 'viewCount';
  direction: 'asc' | 'desc';
}

export interface JobTableRow {
  id: string;
  title: string;
  company: string;
  location: string;
  category: string;
  status: JobStatus;
  postedByName: string;
  postedById: string;
  postedByRole: string;
  createdAt: Date;
  viewCount: number;
  applicationCount?: number;
  source?: string;
}

export interface BulkUploadRow {
  rowNumber: number;
  data: Partial<JobFormData>;
  errors?: string[];
  warnings?: string[];
  isDuplicate?: boolean;
}

export interface BulkUploadParseResults {
  totalRows: number;
  validRows: BulkUploadRow[];
  invalidRows: BulkUploadRow[];
  duplicates: BulkUploadRow[];
}

export interface BulkUploadState {
  uploadedFile: File | null;
  parseResults: BulkUploadParseResults | null;
  importStatus: 'idle' | 'parsing' | 'validating' | 'importing' | 'completed' | 'failed';
  importedJobs: string[];
  errors: string[];
}

export interface JobsListResponse {
  jobs: Job[];
  pagination: PaginationConfig;
  /**
   * Present only for preference-filtered requests (`matchPreferences`). `false`
   * means the customer has set no preferences yet (prompt them to configure);
   * `true` with an empty `jobs` list means their preferences matched nothing.
   */
  hasPreferences?: boolean;
  /**
   * The customer's preferred job types / work modes (canonical enum values),
   * present on preference-filtered responses. The UI restricts its filter
   * dropdowns to these, since the list is already hard-filtered to them.
   */
  preferredJobTypes?: string[];
  preferredWorkModes?: string[];
  /**
   * The customer's plan cap on how many new matching jobs become visible per day
   * (present on preference-filtered responses). Lets the UI explain why the browse
   * count is a subset of the full match set.
   */
  dailyNewJobsVisible?: number;
}

export interface ScrapingQueueResponse {
  queueItems: ScrapingQueueItem[];
  stats: QueueStats;
  pagination: PaginationConfig;
}

export interface DuplicatesResponse {
  duplicateGroups: DuplicateGroup[];
  pagination: PaginationConfig;
}

export interface AssignedJob {
  recommendationId: string;
  assignmentType: AssignmentType;
  recommendationStatus: RecommendationStatus;
  reason?: string;
  assignedAt: string;
  updatedAt: string;
  assignedBy?: string;
  assignedByName?: string;
  id: string;
  title: string;
  company: string;
  location: string;
  industry?: string;
  jobType: JobType;
  workMode: WorkMode;
  category?: string;
  jobStatus: JobStatus;
  jobCreatedAt: string;
}

export interface AssignedCustomer {
  recommendationId: string;
  assignmentType: AssignmentType;
  recommendationStatus: RecommendationStatus;
  reason?: string;
  assignedAt: string;
  updatedAt: string;
  assignedBy?: string;
  assignedByName?: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerLocation?: string;
  leadsRemaining?: number;
}

export interface CreateJobRequest {
  title: string;
  company: string;
  location: string;
  industry?: string;
  jobType: JobType;
  workMode: WorkMode;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  category?: string;
  skills?: string[];
  experienceMin?: number;
  experienceMax?: number;
  qualifications?: string[];
  description: string;
  requirements?: string;
  benefits?: Record<string, any>;
  applicationUrl: string;
  expiryDate?: Date;
  status?: JobStatus;
  source?: string;
}

export interface UpdateJobRequest {
  title?: string;
  company?: string;
  location?: string;
  industry?: string;
  jobType?: JobType;
  workMode?: WorkMode;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  category?: string;
  skills?: string[];
  experienceMin?: number;
  experienceMax?: number;
  qualifications?: string[];
  description?: string;
  requirements?: string;
  benefits?: Record<string, any>;
  applicationUrl?: string;
  expiryDate?: Date;
  status?: JobStatus;
  source?: string;
}

/** Job with the posting employee's full details joined */
export interface JobWithEmployee {
  id: string;
  jobKey?: number;
  title: string;
  company: string;
  location: string;
  industry: string | null;
  jobType: JobType;
  workMode: WorkMode;
  salaryMin: number | null;
  salaryMax: number | null;
  salaryCurrency: string;
  category: string | null;
  skills: string[];
  experienceMin: number | null;
  experienceMax: number | null;
  qualifications: string[];
  description: string;
  requirements: string | null;
  benefits: Record<string, any> | null;
  applicationUrl: string;
  expiryDate: Date | null;
  status: JobStatus;
  postedBy: string;
  source: string | null;
  viewCount: number;
  createdAt: Date;
  updatedAt: Date;
  expiredAt: Date | null;
  expiredBy: string | null;
  suggestedCategory: string | null;
  categoryConfidence: number | null;
  postedByEmployee: { id: string; name: string; role: string };
}

export interface BulkStatusUpdateRequest {
  jobIds: string[];
  status: JobStatus;
}

export interface BulkStatusUpdateResponse {
  updated: number;
  failed: number;
  errors?: { jobId: string; error: string }[];
}

export interface JobDuplicate {
  id: string;
  jobId: string;
  originalJobId: string;
  similarityScore: number;
  status: DuplicateStatus;
  resolvedBy: string | null;
  resolvedAt: Date | null;
  createdAt: Date;
  comments: string | null;
}

export interface DuplicateCheckRequest {
  title: string;
  company: string;
  location: string;
  description: string;
}

export interface DuplicateCheckResponse {
  isDuplicate: boolean;
  matches: DuplicateMatch[];
}

export interface ResolveDuplicatesRequest {
  action: 'keep' | 'delete' | 'not_duplicate';
  keptJobId?: string;
  comments?: string;
}

export interface MergeDuplicatesRequest {
  jobIds: string[];
  mergedData: CreateJobRequest;
}

export interface CsvRow {
  rowNumber: number;
  data: Partial<CreateJobRequest>;
  errors?: string[];
  warnings?: string[];
  isDuplicate?: boolean;
  duplicateMatches?: DuplicateMatch[];
}

export interface ParseCsvResponse {
  totalRows: number;
  validRows: CsvRow[];
  invalidRows: CsvRow[];
  duplicates: CsvRow[];
}

export interface ImportJobsRequest {
  rows: CreateJobRequest[];
}

export interface ImportJobsResponse {
  imported: number;
  failed: number;
  jobIds: string[];
  errors?: { row: number; error: string }[];
}

export interface JobCategories { categories: string[] }
export interface JobSkills { skills: string[] }
export interface JobLocations { locations: string[] }
export interface JobIndustries { industries: string[] }

export interface FetchJobsParams {
  page?: number;
  limit?: number;
  search?: string;
  jobType?: JobType;
  workMode?: WorkMode;
  status?: JobStatus;
  /** When true, restrict the listing to jobs matching the customer's preferences. */
  matchPreferences?: boolean;
}

/**
 * A job related to the one being viewed — nearest neighbour by embedding, so it
 * describes the JOB on screen, not the viewer. Distinct from a match score,
 * which measures a job against a customer's profile.
 */
export interface SimilarJob {
  id: string;
  title: string;
  company: string;
  location: string;
  workMode: WorkMode;
  jobType: JobType;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  companyLogo?: string;
  postedDate?: Date | null;
  createdAt: Date;
  /** Cosine similarity to the source job, 0-1. Higher is closer. */
  similarity: number;
}
